import { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChecklistMutations, useChecklists } from '@/hooks/useTareasDetalle';
import type { Checklist, TareaDetalle } from '@/types/tareas';

const campo = 'px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300';

export function ChecklistsPanel({ tarea }: { tarea: TareaDetalle }) {
  const { data: checklists = [], isLoading } = useChecklists(tarea.id);
  const mut = useChecklistMutations(tarea.id, tarea.tableroId);
  const [nuevoTitulo, setNuevoTitulo] = useState('');

  const crear = (e: React.FormEvent) => {
    e.preventDefault();
    const titulo = nuevoTitulo.trim();
    if (!titulo) return;
    mut.crear.mutate(titulo, { onSuccess: () => setNuevoTitulo('') });
  };

  return (
    <div className="space-y-5">
      {isLoading && <p className="text-sm text-text-secondary">Cargando…</p>}
      {!isLoading && checklists.length === 0 && (
        <p className="text-sm text-text-secondary">Esta tarea aún no tiene checklists. Crea uno para dividirla en pasos.</p>
      )}

      {checklists.map(c => <ChecklistBloque key={c.id} checklist={c} mut={mut} />)}

      <form onSubmit={crear} className="flex gap-2">
        <input
          value={nuevoTitulo}
          onChange={e => setNuevoTitulo(e.target.value)}
          maxLength={150}
          placeholder="Título del nuevo checklist"
          className={cn(campo, 'flex-1 min-w-0')}
        />
        <button
          type="submit"
          disabled={!nuevoTitulo.trim() || mut.crear.isPending}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-sm font-medium disabled:opacity-50"
        >
          <Plus size={14} /> Agregar checklist
        </button>
      </form>
      <p className="text-[11px] text-text-secondary">Los títulos y los elementos no se cifran: no escribas datos clínicos aquí.</p>
    </div>
  );
}

function ChecklistBloque({ checklist, mut }: { checklist: Checklist; mut: ReturnType<typeof useChecklistMutations> }) {
  const [texto, setTexto] = useState('');
  const [confirmando, setConfirmando] = useState(false);

  const total = checklist.items.length;
  const hechos = checklist.items.filter(i => i.completado).length;
  const porcentaje = total ? Math.round((hechos / total) * 100) : 0;

  const agregar = (e: React.FormEvent) => {
    e.preventDefault();
    const valor = texto.trim();
    if (!valor) return;
    mut.crearItem.mutate({ checklistId: checklist.id, texto: valor }, { onSuccess: () => setTexto('') });
  };

  return (
    <section className="rounded-xl border border-border p-4">
      <div className="flex items-center gap-2">
        <input
          defaultValue={checklist.titulo}
          maxLength={150}
          onBlur={e => {
            const titulo = e.target.value.trim();
            if (titulo && titulo !== checklist.titulo) mut.renombrar.mutate({ id: checklist.id, titulo });
            else e.target.value = checklist.titulo;
          }}
          className="flex-1 min-w-0 font-semibold text-text-primary bg-transparent rounded px-1 -mx-1 focus:outline-none focus:ring-2 focus:ring-primary-300"
          aria-label="Título del checklist"
        />
        <span className="text-xs text-text-secondary tabular-nums">{hechos}/{total}</span>
        {confirmando ? (
          <span className="flex items-center gap-1">
            <button onClick={() => { mut.eliminar.mutate(checklist.id); setConfirmando(false); }} className="px-2 py-1 rounded-md bg-red-500 text-white text-xs">Eliminar</button>
            <button onClick={() => setConfirmando(false)} className="px-2 py-1 rounded-md border border-gray-200 text-xs">Cancelar</button>
          </span>
        ) : (
          <button onClick={() => setConfirmando(true)} className="p-1.5 text-gray-400 hover:text-red-500" aria-label="Eliminar checklist">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {total > 0 && (
        <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={porcentaje} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${porcentaje}%` }} />
        </div>
      )}

      <ul className="mt-3 space-y-1">
        {checklist.items.map(item => (
          <li key={item.id} className="group flex items-center gap-2">
            <input
              type="checkbox"
              checked={item.completado}
              onChange={e => mut.actualizarItem.mutate({ checklistId: checklist.id, itemId: item.id, completado: e.target.checked })}
              className="w-4 h-4 rounded accent-emerald-500 flex-shrink-0"
              aria-label={`Marcar "${item.texto}"`}
            />
            <input
              defaultValue={item.texto}
              maxLength={300}
              onBlur={e => {
                const valor = e.target.value.trim();
                if (valor && valor !== item.texto) mut.actualizarItem.mutate({ checklistId: checklist.id, itemId: item.id, texto: valor });
                else e.target.value = item.texto;
              }}
              className={cn(
                'flex-1 min-w-0 text-sm bg-transparent rounded px-1 focus:outline-none focus:ring-2 focus:ring-primary-300',
                item.completado ? 'line-through text-text-secondary' : 'text-text-primary',
              )}
              aria-label="Elemento"
            />
            <button
              onClick={() => mut.eliminarItem.mutate({ checklistId: checklist.id, itemId: item.id })}
              className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100"
              aria-label="Quitar elemento"
            >
              <X size={14} />
            </button>
          </li>
        ))}
      </ul>

      <form onSubmit={agregar} className="mt-3 flex gap-2">
        <input value={texto} onChange={e => setTexto(e.target.value)} maxLength={300} placeholder="Agregar elemento" className={cn(campo, 'flex-1 min-w-0')} />
        <button type="submit" disabled={!texto.trim() || mut.crearItem.isPending} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 disabled:opacity-50">
          Agregar
        </button>
      </form>
    </section>
  );
}
